/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { mergeRefs } from '@react-aria/utils';
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef, type ReactElement, useEffect, useRef } from 'react';
import {
  type ContextValue,
  Provider,
  TagGroup as TagGroupCore,
  type TagGroupProps as TagGroupCoreProps,
  TagList as TagListCore,
  type TagListProps as TagListCoreProps,
  useContextProps
} from 'react-aria-components';
import { useScrollFades } from '../../hooks/use-scroll-fades/use-scroll-fades.hook';
import { FilterListItemContext } from '../filter-list-item/filter-list-item.component';
import type { TagProps } from '../tag/tag.component';

interface FilterListProps<T = object> extends Omit<TagGroupCoreProps, 'style' | 'className' | 'children'> {
  /** The size of the filter list items. (Default: 28) */
  size?: TagProps['size'];
  /** The variant of the filter list items. (Default: 28) */
  variant?: TagProps['variant'];
  /** The items to render in the filter list. */
  items?: TagListCoreProps<T>['items'];
  /** The children of the filter list (render function or FilterListItem elements). */
  children?: TagListCoreProps<T>['children'];
  /** Optional custom styles for the container. */
  style?: stylex.StyleXStyles;
}

const FilterListContext = createContext<ContextValue<FilterListProps, HTMLDivElement>>({});

const FilterList = forwardRef((props: FilterListProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, FilterListContext);

  const {
    style,
    size = 32,
    variant = 'quaternary',
    items,
    children,
    ...coreProps
  } = props;

  const listRef = useRef<HTMLDivElement>(null);
  const { containerRef, getContainerStyle } = useScrollFades<HTMLDivElement>();
  const mergedListRef = mergeRefs(listRef, containerRef);

  // Auto-scroll to the right when items change to show newly added filters
  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    const observer = new MutationObserver(() => listElement.scrollLeft = listElement.scrollWidth);

    observer.observe(listElement, { childList: true, subtree: true });

    listElement.scrollLeft = listElement.scrollWidth;

    return () => observer.disconnect();
  }, [items]);

  return (
    <TagGroupCore
      ref={ref}
      aria-label="Active filters"
      {...stylex.props(styles.base, style)}
      {...coreProps}>
      <Provider values={[[FilterListItemContext, { size, variant }]]}>
        <TagListCore
          ref={mergedListRef}
          items={items}
          style={getContainerStyle()}
          {...stylex.props(listStyles.base)}>
          {children}
        </TagListCore>
      </Provider>
    </TagGroupCore>
  );
}) as <T>(p: FilterListProps<T> & { ref?: ForwardedRef<HTMLDivElement> }) => ReactElement;

const styles = stylex.create({
  base: {
    gap: 8,
    display: 'flex',
    flexDirection: 'column',
  },
});

const listStyles = stylex.create({
  base: {
    gap: 8,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    scrollbarWidth: 'none',
    overflowX: 'auto',
  },
});

export { FilterList, FilterListContext, type FilterListProps };
